/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/react-in-jsx-scope */
import ContainerWithSidebar from "@/components/Sidebar";
import {
  allExtensionsString,
  imageFileTypes,
  profileSidebarMenuList,
} from "@/config";
import { IPaymentMethod } from "@/interfaces";
import { IPaymentMethodAttribute } from "@/interfaces/common";
import Store from "@/store";
import CommonStore from "@/store/common";
import { UserStore } from "@/store/user";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { autorun } from "mobx";
import { inject, observer } from "mobx-react";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Close } from "@mui/icons-material";

const PaymentCreateEditPage = inject(
  Store.type.commonStore,
  Store.type.userStore,
)(
  observer((props: any) => {
    const commonStore = props[Store.type.commonStore] as CommonStore;
    const userStore = props[Store.type.userStore] as UserStore;

    const [selectedPaymentMethod, setSelectedPaymentMethod] =
      useState<IPaymentMethod>();
    const { paymentId } = useParams();
    const [loading, setLoading] = useState<boolean>(false);
    const theme = useTheme();
    const navigate = useNavigate();
    const [title, setTitle] = useState("Add Payment");
    const [attributes, setAttributes] = useState<
      {
        attribute_id: string;
        attribute_value: string;
        error: boolean;
        fileName?: string;
      }[]
    >([]);

    useEffect(() => {
      if (paymentId) {
        setTitle("Edit Payment");
      }
      const disposeAutorun = autorun(async () => {
        const userId = userStore.userId;
        if (userId) {
          userStore.fetchMyPayments(userId);
        }
      });
      return () => {
        disposeAutorun();
      };
    }, [userStore, paymentId]);

    useEffect(() => {
      const payment = userStore.myPayments?.find(
        (payment) => payment.id === paymentId,
      );
      if (payment) {
        setSelectedPaymentMethod(
          commonStore.paymentMethods.find(
            (method) => method.payment_id === payment.payment_method_id,
          ),
        );
        setAttributes(
          payment.payment_attributes.map((attr) => ({
            attribute_id: attr.payment_method_attribute_id,
            attribute_value: attr.attribute_value,
            error: false,
          })),
        );
      }
    }, [userStore.myPayments]);
    const handleChange = (event: any) => {
      setSelectedPaymentMethod(
        commonStore.paymentMethods.find(
          (method) => method.payment_id === event.target.value,
        ),
      );
    };
    const updateAttributes = (
      attribute: IPaymentMethodAttribute,
      value: string,
      fileName?: string,
    ) => {
      if (attributes.find((attr) => attr.attribute_id === attribute.id)) {
        setAttributes(
          attributes.map((attr) => {
            if (attr.attribute_id === attribute.id) {
              return {
                attribute_id: attribute.id,
                attribute_value: value,
                error: false,
                fileName,
              };
            }
            return attr;
          }),
        );
      } else {
        setAttributes([
          ...attributes,
          {
            attribute_id: attribute.id,
            attribute_value: value,
            error: false,
            fileName,
          },
        ]);
      }
    };

    const handleAttributeChange = (
      e: React.ChangeEvent<HTMLInputElement>,
      attribute: IPaymentMethodAttribute,
      isFile: boolean,
    ) => {
      let value = e.target.value;
      let fileName: string | undefined;
      if (value) {
        attribute.error = false;
      }
      if (isFile) {
        if (e.target.files && e.target.files.length > 0) {
          const selectedFile = e.target.files[0];
          // validate file size 500kb
          if (selectedFile.size > 500000) {
            enqueueSnackbar("File size is too large", { variant: "error" });
            return;
          }

          if (!imageFileTypes.includes(selectedFile.type)) {
            enqueueSnackbar(
              `Please select an image file. (${allExtensionsString})`,
              {
                variant: "error",
              },
            );
            return;
          }

          const reader = new FileReader();
          reader.onloadend = () => {
            value = reader.result as string;
            fileName = selectedFile.name;
            updateAttributes(attribute, value, fileName);
          };
          reader.readAsDataURL(selectedFile);
        }
      } else {
        updateAttributes(attribute, value);
      }
    };

    const handleSave = () => {
      try {
        setLoading(true);
        if (userStore.userId) {
          if (selectedPaymentMethod?.attributes)
            for (let i = 0; i < selectedPaymentMethod?.attributes.length; i++) {
              if (selectedPaymentMethod?.attributes[i].required) {
                if (
                  !attributes.find(
                    (attr) =>
                      attr.attribute_id ===
                      selectedPaymentMethod?.attributes[i].id,
                  )?.attribute_value
                ) {
                  selectedPaymentMethod.attributes[i].error = true;
                }
              }
            }
          if (
            selectedPaymentMethod?.attributes.find(
              (attr) => attr.error === true,
            )
          ) {
            setLoading(false);
            return;
          }

          if (paymentId) {
            userStore.updatePayment({
              payment_id: paymentId,

              attributes: attributes.map((attr) => ({
                payment_method_attribute_id: attr.attribute_id,
                attribute_value: attr.attribute_value,
              })),
            });
          } else {
            userStore.createPayment({
              payment_method_id: selectedPaymentMethod?.payment_id || "",
              label: "test",
              attributes: attributes.map((attr) => ({
                payment_method_attribute_id: attr.attribute_id,
                attribute_value: attr.attribute_value,
              })),
            });
          }
          enqueueSnackbar("Payment method saved", { variant: "success" });

          userStore.fetchMyPayments(userStore.userId).then(() => {
            setTimeout(() => {
              setLoading(false);
              navigate("/profile/payments");
            }, 2000);
          });
        }
      } catch (e) {
        console.error(e);
        setLoading(false);
        enqueueSnackbar("Something went wrong", { variant: "error" });
      }
    };
    return (
      <>
        <ContainerWithSidebar menu={profileSidebarMenuList}>
          <Container
            maxWidth="lg"
            sx={{
              py: "82px",
              [theme.breakpoints.up("lg")]: { maxWidth: "lg", py: "82px" },
            }}
          >
            <Card>
              <Grid container spacing={2}>
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    minWidth: "440px",
                  }}
                >
                  <Stack
                    spacing={4}
                    sx={{
                      minWidth: "440px",
                      justifyContent: "center",

                      display: "flex",
                    }}
                  >
                    <Typography
                      variant="h2"
                      sx={{
                        width: "100%",
                        textAlign: "center",
                      }}
                    >
                      {title}
                    </Typography>
                    {!paymentId && (
                      <>
                        <FormControl fullWidth>
                          <Select
                            sx={{ minWidth: 300 }}
                            variant="outlined"
                            value={selectedPaymentMethod?.payment_id || ""}
                            input={<OutlinedInput />}
                            displayEmpty
                            renderValue={(selected: any) => {
                              if (selected === "") {
                                return <em>Choose Payment Method</em>;
                              }
                              return commonStore.paymentMethods.find(
                                (method) => method.payment_id === selected,
                              )?.payment_name;
                            }}
                            onChange={handleChange}
                          >
                            <MenuItem disabled value="">
                              <em>Choose Payment Method</em>
                            </MenuItem>
                            {commonStore.paymentMethods.map((method) => (
                              <MenuItem
                                key={method.payment_id}
                                value={method.payment_id}
                              >
                                {method.payment_name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </>
                    )}
                    {paymentId && (
                      <InputLabel htmlFor="payment-method">
                        {selectedPaymentMethod?.payment_name}
                      </InputLabel>
                    )}
                    {selectedPaymentMethod &&
                      selectedPaymentMethod.attributes.map(
                        (attribute, index) => {
                          const file = attributes.find(
                            (attr) => attr.attribute_id === attribute.id,
                          );
                          return (
                            <Grid key={index} item xs={12}>
                              {attribute.is_image ? (
                                <>
                                  <Typography variant="body1">
                                    {attribute.attribute_label} (Max 500kb)
                                  </Typography>
                                  <Box
                                    position="relative"
                                    sx={{
                                      "&:hover fieldset.MuiOutlinedInput-notchedOutline, .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                                        {
                                          border: !file
                                            ? "2px solid #BEC6D6 !important"
                                            : undefined,
                                        },
                                    }}
                                  >
                                    {!file && (
                                      <>
                                        <TextField
                                          type="file"
                                          inputProps={{
                                            id: `file-input-${attribute.attribute_name}`,
                                            onChange: (
                                              e: React.ChangeEvent<HTMLInputElement>,
                                            ) =>
                                              handleAttributeChange(
                                                e,
                                                attribute,
                                                true,
                                              ),
                                            accept: imageFileTypes.join(", "),
                                          }}
                                          sx={{ display: "none" }}
                                        />
                                        <Box
                                          width="100%"
                                          height="calc(100% - 14px)"
                                          bottom="0"
                                          component="label"
                                          htmlFor={`file-input-${attribute.attribute_name}`}
                                          position="absolute"
                                          sx={{
                                            borderRadius:
                                              theme.borderRadius.large,
                                            "&:hover": { cursor: "pointer" },
                                          }}
                                          zIndex="1"
                                        ></Box>
                                      </>
                                    )}
                                    <TextField
                                      variant="outlined"
                                      fullWidth
                                      type="text"
                                      value={file?.fileName ?? ""}
                                      disabled={true}
                                      placeholder="Click to upload image"
                                      InputProps={{
                                        startAdornment: !!file && (
                                          <Box
                                            component="img"
                                            src={file?.attribute_value}
                                            maxWidth="160px"
                                            maxHeight="160px"
                                            sx={{ objectFit: "cover" }}
                                          />
                                        ),
                                        endAdornment: !!file && (
                                          <IconButton
                                            onClick={() =>
                                              setAttributes(
                                                attributes.filter(
                                                  (attr) =>
                                                    attr.attribute_id !==
                                                    attribute.id,
                                                ),
                                              )
                                            }
                                          >
                                            <Close />
                                          </IconButton>
                                        ),
                                      }}
                                      sx={{
                                        "& .MuiInputBase-root": {
                                          pl: file ? "0" : undefined,
                                        },
                                        "& .MuiInputBase-input, & input.MuiInputBase-input.Mui-disabled":
                                          {
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            WebkitTextFillColor: "initial",
                                          },
                                      }}
                                    />
                                  </Box>
                                </>
                              ) : (
                                <TextField
                                  inputProps={{
                                    accept: imageFileTypes.join(", "),
                                  }}
                                  fullWidth
                                  size="small"
                                  error={attribute.error}
                                  required={attribute.required}
                                  value={
                                    attributes.find(
                                      (attr) =>
                                        attr.attribute_id === attribute.id,
                                    )?.attribute_value || ""
                                  }
                                  onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>,
                                  ) =>
                                    handleAttributeChange(e, attribute, false)
                                  }
                                  label={attribute.attribute_label}
                                />
                              )}
                            </Grid>
                          );
                        },
                      )}
                    {selectedPaymentMethod && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-around",
                          flexDirection: "row",
                        }}
                      >
                        <Button
                          variant="outlined"
                          size="large"
                          onClick={() => navigate("/profile/payments")}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="large"
                          disabled={selectedPaymentMethod === undefined}
                          variant="contained"
                          color="primary"
                          onClick={handleSave}
                        >
                          Save
                        </Button>
                      </div>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Card>
          </Container>
        </ContainerWithSidebar>
        <Dialog
          open={loading}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogContent>
            <CircularProgress />
          </DialogContent>
        </Dialog>
      </>
    );
  }),
);
export default PaymentCreateEditPage;
